import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { getAuthToken } from "../services/clientConfig";
import { useAuth } from "../contexts/AuthContext";

const MC_BASE_URL = process.env.REACT_APP_MC_LINK;

/** Set on <body> while the Mystery Client drill-down sidebar covers the left side. */
const SIDEBAR_OPEN_CLASS = "mc-sidebar-open";

const MC_ORIGIN = (() => {
  try {
    return new URL(MC_BASE_URL).origin;
  } catch {
    return null;
  }
})();

function MysteryClientIframe({ visible, onIframeLoad }) {
  const iframeRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const { user } = useAuth();

  // The dashboard must open on the client we are logged in as; without it the
  // dashboard falls back to its own default and shows an unrelated city.
  // client_type is the last resort: the dashboard also matches it against clientId.
  const clientType = localStorage.getItem("client_type");
  const company = user?.company
    || localStorage.getItem("company")
    || (clientType && clientType !== "default" ? clientType : "")
    || "";

  const iframeSrc = useMemo(() => {
    const authToken = getAuthToken() || localStorage.getItem("auth_token");

    const params = new URLSearchParams();

    if (company) {
      params.set("mc_client", company);
    }

    if (authToken) {
      params.set("auth_token", authToken);
    }

    return `${MC_BASE_URL}?${params.toString()}`;
  }, [company]);

  useEffect(() => {
    setLoaded(false);
  }, [iframeSrc]);

  const handleLoad = useCallback(() => {
    setLoaded(true);
    if (onIframeLoad) onIframeLoad();
  }, [onIframeLoad]);

  const postToIframe = useCallback((type, payload = {}) => {
    const frame = iframeRef.current?.contentWindow;
    if (!frame) return;
    frame.postMessage({ type, ...payload }, MC_ORIGIN || "*");
  }, []);

  // The iframe stays mounted between visits, so ask it for a clean default view
  // (all cities, sidebar closed) each time the user comes back to it. The client
  // travels with the message too: the URL was built when the iframe first loaded,
  // which can be before we know who is logged in.
  useEffect(() => {
    if (!visible || !loaded) return;
    postToIframe("mystery-client:show", { company });
  }, [visible, loaded, company, postToIframe]);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (MC_ORIGIN && event.origin !== MC_ORIGIN) return;
      if (event.data?.source !== "mystery-client") return;

      if (event.data.type === "mystery-client:sidebar") {
        document.body.classList.toggle(SIDEBAR_OPEN_CLASS, Boolean(event.data.open));
      } else if (event.data.type === "mystery-client:ready") {
        postToIframe("mystery-client:client", { company });
        postToIframe("mystery-client:request-state");
      }
    };

    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      document.body.classList.remove(SIDEBAR_OPEN_CLASS);
    };
  }, [company, postToIframe]);

  // Leaving the dashboard must never leave our own overlays hidden.
  useEffect(() => {
    if (!visible) document.body.classList.remove(SIDEBAR_OPEN_CLASS);
  }, [visible]);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100vh",
        zIndex: visible ? 99999 : -1,
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? "auto" : "none",
      }}
    >
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        onLoad={handleLoad}
        style={{ width: "100%", height: "100%", border: "none" }}
        title="Mystery Client"
      />
    </div>
  );
}

export default MysteryClientIframe;
